import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Mail, Phone, MapPin, MessageCircle, Edit3, Download,
  AlertTriangle, Loader2, Clock, CheckCircle2, PieChart as PieIcon,
  TrendingUp, Users as UsersIcon, Star, Receipt, FolderKanban,
  UserCircle2, FileText, Lock,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import ClientReportDialog from '@/components/ClientReportDialog';
import ProposalsTab from '@/components/ProposalsTab';
import { toast } from 'sonner';

// ---------- period helpers ----------
const iso = (d) => d.toISOString().slice(0, 10);
const PRESETS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_3_months', label: 'Last 3 months' },
  { key: 'last_6_months', label: 'Last 6 months' },
  { key: 'this_year', label: 'This year' },
  { key: 'all_time', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];
function rangeForPreset(key) {
  const today = new Date();
  const y = today.getFullYear(); const m = today.getMonth();
  if (key === 'this_month') return { from: iso(new Date(y, m, 1)), to: iso(today) };
  if (key === 'last_month') return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (key === 'last_3_months') return { from: iso(new Date(y, m - 2, 1)), to: iso(today) };
  if (key === 'last_6_months') return { from: iso(new Date(y, m - 5, 1)), to: iso(today) };
  if (key === 'this_year') return { from: iso(new Date(y, 0, 1)), to: iso(today) };
  if (key === 'all_time') return { from: '2000-01-01', to: iso(today) };
  return null;
}

const STATUS_COLORS = {
  Ongoing: '#f59e0b',
  Submitted: '#2B5EA1',
  Accepted: '#8b5cf6',
  Completed: '#00A859',
};
const FORMAT_COLORS = ['#2B5EA1', '#00A859', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9', '#f43f5e', '#14b8a6'];

const niceDate = (iso10) => {
  if (!iso10) return '';
  const [y, m, d] = iso10.split('-').map(Number);
  if (!y) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};
const niceMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  if (!y) return ym;
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-GB', {
    month: 'short', year: '2-digit',
  });
};

const initials = (s) => (s || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

function Delta({ now, prev }) {
  if (prev == null || prev === 0 || now == null) return null;
  const diff = ((now - prev) / Math.max(1, prev)) * 100;
  const sign = diff > 0 ? '+' : '';
  const cls = diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-600' : 'text-slate-500';
  return <span className={`text-[11px] font-medium ${cls}`}>{sign}{diff.toFixed(0)}% vs prev</span>;
}

function Tile({ label, value, suffix, delta, loading, icon: Icon, dark, tint }) {
  const bg = dark ? 'bg-slate-900 text-white border-slate-800'
                  : tint === 'orange' ? 'bg-amber-50 border-amber-200'
                  : 'shine-card';
  const valCls = dark ? 'text-white' : 'text-slate-900';
  const lblCls = dark ? 'text-slate-300' : 'text-slate-500';
  return (
    <Card className={`${bg} p-4 rounded-xl`}>
      <div className="flex items-center justify-between">
        <div className={`text-[11px] uppercase tracking-wider ${lblCls}`}>{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${dark ? 'text-slate-400' : 'text-slate-400'}`} />}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-20 mt-2" />
      ) : (
        <div className={`text-2xl font-extrabold ${valCls} mt-1`}>
          {value ?? '—'}{suffix ? <span className={`text-sm font-medium ${dark ? 'text-slate-300' : 'text-slate-500'} ml-1`}>{suffix}</span> : null}
        </div>
      )}
      {delta}
    </Card>
  );
}

function BlockHeader({ title, caption, icon: Icon }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-slate-500" />}
        <h3 className="font-bold text-slate-900">{title}</h3>
      </div>
      {caption && <div className="text-[11px] text-slate-500">{caption}</div>}
    </div>
  );
}

function EmptyLine({ children }) {
  return <div className="text-xs text-slate-500 italic py-6 text-center">{children}</div>;
}

const TABS = ['overview', 'work', 'proposals', 'messages', 'invoices', 'contacts'];
const TAB_LABELS = { overview: 'Overview', work: 'Work', proposals: 'Proposals', messages: 'Messages', invoices: 'Invoices', contacts: 'Contacts' };
const TAB_PERMS = { proposals: 'proposals_view' };

export default function ClientProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, has } = useAuth();
  const canEdit = has('clients_edit');
  const canSeeMoney = has('see_money') || has('finance_reviewer') || has('finance_admin');
  const [preset, setPreset] = useState('last_6_months');
  const [range, setRange] = useState(() => rangeForPreset('last_6_months'));
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  const periodCaption = `${niceDate(range?.from)} – ${niceDate(range?.to)}`;
  const changePreset = (v) => {
    setPreset(v);
    const r = rangeForPreset(v);
    if (r) setRange(r);
  };

  useEffect(() => {
    if (!id || !range?.from || !range?.to) return;
    let cancelled = false;
    setLoading(true); setError('');
    (async () => {
      try {
        const res = await api.get(`/clients/${id}/overview`, {
          params: { from: range.from, to: range.to },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || 'Could not load profile';
          setError(detail);
          if (e?.response?.status === 403) toast.error('Not allowed to open this client profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, range?.from, range?.to]);

  // Fetch logo blob
  useEffect(() => {
    if (!data?.client?.has_logo) { setLogoUrl(null); return; }
    let active = true; let url = null;
    (async () => {
      try {
        const res = await api.get(`/clients/${id}/logo`, { responseType: 'blob' });
        url = URL.createObjectURL(res.data);
        if (active) setLogoUrl(url);
      } catch {}
    })();
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [id, data?.client?.has_logo]);

  // Open an internal mail thread with this client linked.
  const openMessage = () => {
    navigate(`/messages?compose=1&client=${id}`);
  };

  const client = data?.client || {};
  const tiles = data?.tiles || {};
  const prev = data?.prev_tiles || {};
  const moneyHidden = !!data?.money_hidden;
  const hoursHidden = !!data?.hours_hidden;
  const showHours = !hoursHidden && tiles.hours !== undefined;
  const showMoney = !moneyHidden && !!data?.money;

  const statusPieData = useMemo(() => {
    const s = data?.status_split || {};
    return Object.entries(s)
      .map(([name, value]) => ({ name, value: value || 0 }))
      .filter((r) => r.value > 0);
  }, [data]);

  const formatMixData = useMemo(() => (data?.work_by_format || []).map((r) => ({ name: r.format, value: r.count })), [data]);
  const totalItems = tiles.items || 0;

  const avgPerMonth = useMemo(() => {
    if (!tiles.hours || !data?.hours_over_time?.length) return null;
    return Math.round(tiles.hours / Math.max(1, data.hours_over_time.length));
  }, [tiles, data]);

  if (error && !data) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <Card className="shine-card p-10 rounded-xl">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold mt-3 text-slate-900">Not allowed</h2>
          <p className="text-sm text-slate-500 mt-1">{error}</p>
          <Button className="mt-4" variant="outline" onClick={() => navigate('/clients')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Clients
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={() => navigate('/clients')}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
          data-testid="back-to-clients"
        >
          <ArrowLeft className="h-4 w-4" /> All clients
        </button>
      </div>

      {/* Header + tabs */}
      <Card className="shine-card p-5 rounded-xl">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-16 w-16 rounded-xl bg-white border border-emerald-500/30 flex items-center justify-center overflow-hidden flex-shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt={`${client.company} logo`} className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-2xl font-bold text-emerald-600">{initials(client.company)}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-extrabold text-slate-900 truncate">{client.company || '—'}</h1>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              {client.name && (
                <span className="inline-flex items-center gap-1">
                  <UserCircle2 className="h-3.5 w-3.5" /> {client.name}
                </span>
              )}
              {client.email && (
                <a href={`mailto:${client.email}`} className="hover:text-slate-800 inline-flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" /> {client.email}
                </a>
              )}
              {client.phone && (
                <>
                  <a href={`tel:${client.phone}`} className="hover:text-slate-800 inline-flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" /> {client.phone}
                  </a>
                  <a href={`https://wa.me/${client.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer"
                     className="hover:text-emerald-700 inline-flex items-center gap-1">
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                </>
              )}
              {client.address && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {client.address}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <Button size="sm" variant="outline" onClick={openMessage} data-testid="profile-message-btn">
              <Mail className="h-4 w-4 mr-1" /> Message
            </Button>
            {canEdit && (
              <Button size="sm" variant="outline"
                      onClick={() => navigate(`/clients?edit=${client.id}`)}
                      data-testid="profile-edit-btn">
                <Edit3 className="h-4 w-4 mr-1" /> Edit
              </Button>
            )}
            <Button size="sm" onClick={() => setReportOpen(true)}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="profile-client-report-btn">
              <Download className="h-4 w-4 mr-1" /> Client report
            </Button>
          </div>
        </div>

        {/* Tabs + period */}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
            {TABS.filter((t) => !TAB_PERMS[t] || has(TAB_PERMS[t])).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-selectable
                data-state={tab === t ? 'active' : 'inactive'}
                className={`shine-pill-dark px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  tab === t ? 'text-white' : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`profile-tab-${t}`}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Select value={preset} onValueChange={changePreset}>
              <SelectTrigger className="w-[180px]" data-testid="profile-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {preset === 'custom' && (
              <>
                <Input type="date" className="w-[150px]" value={range?.from || ''}
                       max={range?.to || undefined}
                       onChange={(e) => setRange({ ...range, from: e.target.value })} />
                <Input type="date" className="w-[150px]" value={range?.to || ''}
                       min={range?.from || undefined} max={iso(new Date())}
                       onChange={(e) => setRange({ ...range, to: e.target.value })} />
              </>
            )}
            {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
        </div>
      </Card>

      {/* OVERVIEW TAB */}
      {tab === 'overview' && (
        <>
          <div className={`grid gap-3 ${showMoney ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'}`}>
            <Tile dark icon={FolderKanban} label={`Deliverables · ${(() => {
              const months = Math.max(1, data?.deliverables_per_month?.length || 1);
              return `${months} mo`;
            })()}`} value={tiles.delivered ?? 0}
                  delta={<Delta now={tiles.delivered} prev={prev.delivered} />} loading={loading} />
            {showHours && (
              <Tile icon={Clock} label="Hours spent" value={tiles.hours ?? 0} suffix="h"
                    delta={avgPerMonth ? <span className="text-[11px] text-slate-500">≈ {avgPerMonth} h / month</span> : null}
                    loading={loading} />
            )}
            {hoursHidden && (
              <Tile icon={Lock} label="Hours spent" value="—"
                    delta={<span className="text-[11px] text-slate-500">Restricted by your access</span>}
                    loading={loading} />
            )}
            <Tile icon={Star} label="Avg rating"
                  value={data?.ratings?.average ?? '—'}
                  suffix={data?.ratings?.average ? '/5' : ''}
                  delta={data?.ratings?.count ? <span className="text-[11px] text-slate-500">{data.ratings.count} reviews</span> : null}
                  loading={loading} />
            {data?.avg_turnaround_days != null && (
              <Tile icon={TrendingUp} label="Avg turnaround" value={data.avg_turnaround_days} suffix="d" loading={loading} />
            )}
            {showMoney && (
              <Tile tint="orange" icon={Receipt} label="Outstanding"
                    value={`₹${Number(data.money.outstanding || 0).toLocaleString()}`}
                    loading={loading} />
            )}
            {moneyHidden && (
              <Tile tint="orange" icon={Lock} label="Outstanding" value="—"
                    delta={<span className="text-[11px] text-slate-500">Restricted by your access</span>}
                    loading={loading} />
            )}
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            {/* Deliverables per month: Posts vs Reels & video (stacked) */}
            <Card className="shine-card p-4 rounded-xl lg:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-slate-900">Deliverables per month</h3>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-900" /> Posts</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Reels &amp; video</span>
                </div>
              </div>
              {loading ? <Skeleton className="h-56 w-full" /> : (
                data?.deliverables_per_month?.length ? (
                  <div style={{ width: '100%', height: 240 }}>
                    <ResponsiveContainer>
                      <BarChart data={data.deliverables_per_month.map((b) => ({ ...b, month: niceMonth(b.month) }))}>
                        <XAxis dataKey="month" fontSize={11} stroke="#64748b" />
                        <YAxis fontSize={11} stroke="#64748b" allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="posts" stackId="a" fill="#0f172a" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="reels" stackId="a" fill="#00A859" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <EmptyLine>No work in this period</EmptyLine>
              )}
            </Card>

            {/* Format mix donut */}
            <Card className="shine-card p-4 rounded-xl">
              <BlockHeader title="Format mix" icon={PieIcon} />
              {loading ? <Skeleton className="h-56 w-full" /> : (
                formatMixData.length ? (
                  <div style={{ width: '100%', height: 220, position: 'relative' }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={formatMixData} dataKey="value" nameKey="name"
                             innerRadius={60} outerRadius={88} paddingAngle={2}>
                          {formatMixData.map((e, i) => (
                            <Cell key={i} fill={FORMAT_COLORS[i % FORMAT_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <div className="text-2xl font-extrabold text-slate-900">{totalItems}</div>
                      <div className="text-[11px] text-slate-500">items</div>
                    </div>
                  </div>
                ) : <EmptyLine>No work in this period</EmptyLine>
              )}
              {!!formatMixData.length && (
                <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                  {formatMixData.slice(0, 6).map((f, i) => (
                    <div key={f.name} className="flex items-center gap-1 truncate">
                      <span className="h-2 w-2 rounded-full" style={{ background: FORMAT_COLORS[i % FORMAT_COLORS.length] }} />
                      <span className="truncate text-slate-700">{f.name}</span>
                      <span className="text-slate-500">
                        {totalItems ? Math.round((f.value / totalItems) * 100) : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            {/* Invoiced vs received per month (finance only) */}
            {showMoney && (
              <Card className="shine-card p-4 rounded-xl">
                <BlockHeader title="Invoiced vs received" caption="Finance only" icon={Receipt} />
                {loading ? <Skeleton className="h-48 w-full" /> : (
                  data?.money?.over_time?.length ? (
                    <div style={{ width: '100%', height: 200 }}>
                      <ResponsiveContainer>
                        <BarChart data={data.money.over_time.map((b) => ({ ...b, month: niceMonth(b.month) }))}>
                          <XAxis dataKey="month" fontSize={11} stroke="#64748b" />
                          <YAxis fontSize={11} stroke="#64748b" />
                          <Tooltip formatter={(v) => `₹${Number(v).toLocaleString()}`} />
                          <Bar dataKey="invoiced" fill="#cbd5e1" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="received" fill="#00A859" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="due" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <EmptyLine>No invoices in this period</EmptyLine>
                )}
              </Card>
            )}

            {/* Client rating line */}
            <Card className="shine-card p-4 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-slate-900">Client rating</h3>
                {data?.ratings?.average && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {data.ratings.average} avg
                  </span>
                )}
              </div>
              {loading ? <Skeleton className="h-48 w-full" /> : (
                (data?.ratings?.latest || []).length >= 2 ? (
                  <div style={{ width: '100%', height: 200 }}>
                    <ResponsiveContainer>
                      <LineChart data={[...(data.ratings.latest || [])].reverse().map((r) => ({
                        at: niceMonth((r.at || '').slice(0, 7)),
                        rating: r.rating,
                      }))}>
                        <XAxis dataKey="at" fontSize={11} stroke="#64748b" />
                        <YAxis domain={[0, 5]} fontSize={11} stroke="#64748b" />
                        <Tooltip />
                        <Line type="monotone" dataKey="rating" stroke="#2B5EA1" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : <EmptyLine>Need at least 2 ratings to show a trend</EmptyLine>
              )}
            </Card>

            {/* Hours by team (internal only, needs see_others_hours) */}
            {showHours && data?.hours_by_team?.length > 0 && (
              <Card className="shine-card p-4 rounded-xl">
                <BlockHeader title="Hours by team" caption="Internal only — clients never see hours" icon={UsersIcon} />
                <div className="space-y-2">
                  {data.hours_by_team.map((h, i) => {
                    const max = Math.max(...data.hours_by_team.map((x) => x.hours), 1);
                    const pct = Math.round((h.hours / max) * 100);
                    return (
                      <div key={h.team}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-slate-700">{h.team}</span>
                          <span className="text-slate-500">{h.hours} h</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                          <div className="h-full rounded-full"
                               style={{ width: `${pct}%`, background: FORMAT_COLORS[i % FORMAT_COLORS.length] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
        </>
      )}

      {/* WORK TAB */}
      {tab === 'work' && (
        <>
          <div className="grid gap-3 lg:grid-cols-3">
            <Card className="shine-card p-4 rounded-xl lg:col-span-2">
              <BlockHeader title="Work over time" caption={periodCaption} icon={TrendingUp} />
              {loading ? <Skeleton className="h-56 w-full" /> : (
                data?.work_over_time?.length ? (
                  <div style={{ width: '100%', height: 240 }}>
                    <ResponsiveContainer>
                      <BarChart data={data.work_over_time.map((b) => ({ ...b, month: niceMonth(b.month) }))}>
                        <XAxis dataKey="month" fontSize={11} stroke="#64748b" />
                        <YAxis fontSize={11} stroke="#64748b" allowDecimals={false} />
                        <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Ongoing" stackId="s" fill={STATUS_COLORS.Ongoing} />
                        <Bar dataKey="Submitted" stackId="s" fill={STATUS_COLORS.Submitted} />
                        <Bar dataKey="Accepted" stackId="s" fill={STATUS_COLORS.Accepted} />
                        <Bar dataKey="Completed" stackId="s" fill={STATUS_COLORS.Completed} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <EmptyLine>No work in this period</EmptyLine>
              )}
            </Card>

            <Card className="shine-card p-4 rounded-xl">
              <BlockHeader title="Status split" caption="Current period" icon={PieIcon} />
              {loading ? <Skeleton className="h-56 w-full" /> : (
                statusPieData.length ? (
                  <div style={{ width: '100%', height: 240 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={statusPieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                          {statusPieData.map((e, i) => (
                            <Cell key={i} fill={STATUS_COLORS[e.name] || FORMAT_COLORS[i % FORMAT_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : <EmptyLine>No work in this period</EmptyLine>
              )}
            </Card>
          </div>

          <Card className="shine-card p-4 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <h3 className="font-bold text-slate-900">Recent work</h3>
              </div>
              <Link to={`/reports?client=${encodeURIComponent(id)}`} className="text-xs text-emerald-700 hover:underline">
                View all in Daily Reports →
              </Link>
            </div>
            {loading ? <Skeleton className="h-32 w-full" /> : (
              data?.recent_work?.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500 text-left">
                      <th className="py-2 font-medium">Date</th>
                      <th className="py-2 font-medium">Title</th>
                      <th className="py-2 font-medium">Format</th>
                      {showHours && <th className="py-2 font-medium">Hours</th>}
                      {showHours && <th className="py-2 font-medium">By</th>}
                      <th className="py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_work.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="py-2 text-slate-700 whitespace-nowrap">{niceDate(r.date)}</td>
                        <td className="py-2 text-slate-800 truncate max-w-[280px]">{r.title}</td>
                        <td className="py-2 text-slate-700">{r.format}</td>
                        {showHours && (
                          <td className="py-2 text-slate-700">
                            {r.hours_hidden ? (
                              <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><Lock className="h-3 w-3" /> —</span>
                            ) : `${r.hours ?? 0}h`}
                          </td>
                        )}
                        {showHours && (
                          <td className="py-2 text-slate-700">
                            {r.hours_hidden ? (
                              <span className="text-slate-400 text-xs">—</span>
                            ) : (r.by || '—')}
                          </td>
                        )}
                        <td className="py-2">
                          <Badge variant="outline" style={{ color: STATUS_COLORS[r.status], borderColor: STATUS_COLORS[r.status] }}>
                            {r.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <EmptyLine>No work in this period</EmptyLine>
            )}
          </Card>
        </>
      )}

      {/* MESSAGES TAB */}
      {tab === 'proposals' && (
        <ProposalsTab clientId={id} />
      )}

      {tab === 'messages' && (
        <ClientMessagesTab clientId={id} />
      )}

      {/* INVOICES TAB — finance only */}
      {tab === 'invoices' && (
        showMoney ? (
          <Card className="shine-card p-4 rounded-xl">
            <BlockHeader title="Latest invoices" caption={periodCaption} icon={Receipt} />
            {loading ? <Skeleton className="h-32 w-full" /> : (
              data?.money?.latest_invoices?.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500 text-left">
                      <th className="py-2 font-medium">Invoice</th>
                      <th className="py-2 font-medium">Date</th>
                      <th className="py-2 font-medium">Total</th>
                      <th className="py-2 font-medium">Due</th>
                      <th className="py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.money.latest_invoices.map((inv, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-2 text-slate-800">#{inv.invoice_number || '—'}</td>
                        <td className="py-2 text-slate-700">{niceDate((inv.invoice_date || '').slice(0, 10))}</td>
                        <td className="py-2 text-slate-700">₹{Number(inv.total || 0).toLocaleString()}</td>
                        <td className="py-2 text-slate-700">₹{Number(inv.balance_due || 0).toLocaleString()}</td>
                        <td className="py-2"><Badge variant="outline">{inv.status || '—'}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <EmptyLine>No invoices on file</EmptyLine>
            )}
          </Card>
        ) : (
          <Card className="shine-card p-6 rounded-xl text-center text-sm text-slate-500">
            Finance access required to see invoices.
          </Card>
        )
      )}

      {/* CONTACTS TAB */}
      {tab === 'contacts' && (
        <Card className="shine-card p-4 rounded-xl">
          <BlockHeader title="Portal access" caption={`${(data?.portal_users || []).length} login(s)`} icon={UsersIcon} />
          {loading ? <Skeleton className="h-20 w-full" /> : (
            data?.portal_users?.length ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 text-left">
                    <th className="py-2 font-medium">Name</th>
                    <th className="py-2 font-medium">Email</th>
                    <th className="py-2 font-medium">Last login</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.portal_users.map((u, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2 text-slate-800">{u.name}</td>
                      <td className="py-2 text-slate-700">{u.email}</td>
                      <td className="py-2 text-slate-700">{niceDate((u.last_login_at || '').slice(0, 10)) || 'Never'}</td>
                      <td className="py-2">
                        <Badge variant="outline" className={u.is_active ? 'text-emerald-700 border-emerald-300' : 'text-slate-500 border-slate-300'}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyLine>No client portal users yet</EmptyLine>
          )}
        </Card>
      )}

      <ClientReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        client={client}
      />
    </div>
  );
}

// ----- Messages tab: list internal threads linked to this client -----
function ClientMessagesTab({ clientId }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/messages/threads', { params: { client_id: clientId, limit: 50 } });
        if (!cancelled) setThreads(res.data?.threads || []);
      } catch {} finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  return (
    <Card className="shine-card p-4 rounded-xl">
      <BlockHeader title="Internal threads linked to this client"
                   caption={`${threads.length} thread(s)`}
                   icon={Mail} />
      {loading ? <Skeleton className="h-24 w-full" /> : (
        threads.length ? (
          <div className="divide-y divide-slate-100">
            {threads.map((t) => (
              <Link key={t.id} to={`/messages?thread=${t.id}`}
                    className="flex items-center justify-between py-2 hover:bg-slate-50 px-2 -mx-2 rounded">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {t.subject || t.name || '(no subject)'}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {t.last_sender_name} — {t.last_message_preview}
                  </div>
                </div>
                <div className="text-xs text-slate-400 ml-3">{niceDate((t.updated_at || '').slice(0,10))}</div>
              </Link>
            ))}
          </div>
        ) : <EmptyLine>No threads linked to this client yet</EmptyLine>
      )}
    </Card>
  );
}
